/**
 * HeroFloatingMetrics — decorative floating tiles for the hero gutters.
 *
 * Each tile is a brutalist COLORED square (brand accent fill + white icon and a
 * short metric name) with a hard, 0-blur BLACK offset shadow — matching the
 * Figma floating-tile treatment. The labels are the real, most-important health
 * types StudySync extracts from BOTH Apple HealthKit and Fitbit (see
 * src/constants/metricCategories.ts / dataCoverageCommonMetrics.ts) — Heart
 * Rate, HRV, Sleep, Steps, SpO₂, Calories.
 *
 * Tile fills use the brand's accent palette (Cardinal, Teal, Green) — accents
 * ONLY, never primary surfaces. 3 tiles per gutter, balanced one-of-each-color
 * per side. Yellow is intentionally excluded (white icons under-contrast on it).
 *
 * Purely decorative: the container is aria-hidden + pointer-events-none and is
 * shown on lg+ only (the centered hero column owns the small-screen layout).
 * Float motion is reduced-motion aware.
 */
import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { Activity, Droplet, Flame, Footprints, Heart, Moon } from 'lucide-react';

// Brand accent fills (hex kept inline; the offset shadow stays black via shadow-lg).
const CARDINAL = '#8C1515';
const TEAL = '#00505C';
const GREEN = '#1FA85C';

type MetricTile = {
  /** Short metric name shown on the tile (HR, HRV, …). */
  label: string;
  Icon: LucideIcon;
  /** Tile fill (brand accent). */
  fill: string;
  /** Absolute position (lg gutters, around the centered column). */
  pos: string;
  /** Float amplitude (px) + timing, staggered so tiles drift independently. */
  drift: number;
  duration: number;
  delay: number;
};

// 6 cross-platform (HealthKit + Fitbit) metrics, 3 per gutter. Fills are 2× each
// accent but deliberately scattered (no mirrored rows, no shared bottom color) so
// the two gutters don't read as symmetric.
const TILES: MetricTile[] = [
  // ── left gutter (top → bottom) ──
  { label: 'HR', Icon: Heart, fill: CARDINAL, pos: 'left-[3%] top-[8%]', drift: 12, duration: 6.0, delay: 0 },
  { label: 'SpO₂', Icon: Droplet, fill: GREEN, pos: 'left-[6%] top-[42%]', drift: 10, duration: 6.8, delay: 0.5 },
  { label: 'STEPS', Icon: Footprints, fill: TEAL, pos: 'left-[3%] top-[76%]', drift: 14, duration: 7.4, delay: 0.3 },
  // ── right gutter (top → bottom) ──
  { label: 'HRV', Icon: Activity, fill: GREEN, pos: 'right-[4%] top-[10%]', drift: 13, duration: 6.6, delay: 0.2 },
  { label: 'KCAL', Icon: Flame, fill: TEAL, pos: 'right-[7%] top-[44%]', drift: 10, duration: 7.2, delay: 0.8 },
  { label: 'SLEEP', Icon: Moon, fill: CARDINAL, pos: 'right-[4%] top-[78%]', drift: 11, duration: 7.6, delay: 0.6 },
];

const HeroFloatingMetrics: React.FC = () => {
  const reduceMotion = useReducedMotion();

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 hidden lg:block"
    >
      {TILES.map(({ label, Icon, fill, pos, drift, duration, delay }) => (
        <motion.div
          key={label}
          className={`absolute ${pos}`}
          // Gentle entrance, then perpetual vertical float (disabled for reduced motion).
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={
            reduceMotion ? { opacity: 1 } : { opacity: 1, y: [0, -drift, 0] }
          }
          transition={
            reduceMotion
              ? undefined
              : {
                  opacity: { duration: 0.5, delay: 0.2 + delay },
                  y: { duration, repeat: Infinity, ease: 'easeInOut', delay },
                }
          }
        >
          <div
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 border-2 border-border shadow-lg"
            style={{ backgroundColor: fill }}
          >
            <Icon className="h-6 w-6 text-white" strokeWidth={2} aria-hidden="true" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-white">
              {label}
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default HeroFloatingMetrics;
